<!-- Pills Section -->
<div class="Notification-pills u-spacing-lg-mb">
    <div class="flex Notification-pills--truncate">
        <ce-pill state="default" ce-pill-xs ce-pill-secondary class="u-spacing-sm-mr">{{
				notification().siteName
            }}
        </ce-pill>
        @if (notification().events.length === 1) {
            <ce-pill state="default" ce-pill-xs ce-pill-secondary class="u-spacing-sm-mr">
                {{ notification().vehicleNames.join(', ') }}
            </ce-pill>
        } @else if (notification().events.length > 1) {
            <ce-pill state="default" ce-pill-xs ce-pill-secondary class="u-spacing-sm-mr">
                {{ notification().events.length }} EVs
            </ce-pill>
        }
    </div>
    <div class="flex flex-end">
        @if (notification().archived) {
            <ce-pill [state]="'default'" icon="archive" ce-pill-xs ce-pill-secondary class="u-spacing-sm-mr">Archived</ce-pill>
        }
        @if (!isScheduledSessionEndedUnexpectedly()) {
            @if (notification().eventState === 'active') {
                <ce-pill [state]="'info'" ce-pill-xs ce-pill-secondary>Active</ce-pill>
            } @else {
                <span class="u-text-body-xs u-color-text-secondary">Resolved</span>
            }
        }
    </div>
</div>

<!-- Summary Text -->
<p class="Notification-summary u-text-body-lg">{{ notification().body }}</p>

<!-- Action Button -->
<a
    [routerLink]="detailsRouterLink()"
    [queryParams]="detailsQueryParams()"
    ce-button
    ce-button-small
    ce-button-type-tertiary
    class="u-spacing-xl-mt u-spacing-2xl-mb"
>
    {{ linkLabel() }}
</a>

<!-- Parameter List -->
<ce-parameter-list size="small">
    <ce-parameter-item [orientation]="'horizontal'">
        <span ceParameterLabel>
            @if (hasMultipleEvents()) {
                First event occurred
            } @else {
                Event occurred
            }
        </span>
        <span ceParameterItemValue class="u-text-align-right">{{ notification().eventTimestamp }}</span>
    </ce-parameter-item>
    <ce-parameter-item [orientation]="'horizontal'">
        <span ceParameterLabel>Notification sent</span>
        <span ceParameterItemValue class="u-text-align-right">{{ notification().notificationTimestamp }}</span>
    </ce-parameter-item>
    <ce-parameter-item [orientation]="'horizontal'">
        <span ceParameterLabel>
            @if (hasMultipleEvents()) {
                Events resolved
            } @else {
                Event resolved
            }
        </span>
        @if (isScheduledSessionEndedUnexpectedly()) {
            <span ceParameterItemValue class="u-text-placeholder u-text-align-right">Not applicable</span>
        } @else if (notification().eventResolvedTimestamp) {
            <span ceParameterItemValue class="u-text-align-right">{{ notification().eventResolvedTimestamp }}</span>
        } @else {
            <span ceParameterItemValue class="u-text-placeholder u-text-align-right">Not yet resolved</span>
        }
    </ce-parameter-item>
    @if (showResolutionReason()) {
        <ce-parameter-item [orientation]="'horizontal'">
            <span ceParameterLabel>Resolution reason</span>
            <span ceParameterItemValue class="u-text-align-right">{{ notification().events[0].resolutionReason }}</span>
        </ce-parameter-item>
    }
    <ce-parameter-item [orientation]="'horizontal'">
        <span ceParameterLabel>Site</span>
        <span ceParameterItemValue class="u-text-align-right">{{ notification().siteName }}</span>
    </ce-parameter-item>
    <ce-parameter-item [orientation]="'horizontal'">
        <span ceParameterLabel>
            @if (hasMultipleEvents()) {
                EVs
            } @else {
                EV
            }
        </span>
        <span ceParameterItemValue class="u-text-align-right">{{ notification().vehicleNames.join(', ') }}</span>
    </ce-parameter-item>
    <ce-parameter-item [orientation]="'horizontal'">
        <span ceParameterLabel>Email notification</span>
        <span ceParameterItemValue class="u-text-align-right">
            @if (emailTimestamp()?.timestamp) {
                Sent
            } @else {
                Not sent
            }
        </span>
    </ce-parameter-item>
</ce-parameter-list>

@if (isScheduledSessionEndedUnexpectedly()) {
    <p class="u-text-body-xs u-color-text-secondary u-spacing-md-mt">
        This event type does not have active/resolved states.
    </p>
}

<!-- Per-vehicle details for multiple events -->
@if (hasMultipleEvents()) {
    @for (event of formattedEvents(); let i = $index; track i) {
        <div class="u-spacing-2xl-mt">
            <h2 class="u-text-title-xs u-spacing-sm-mb Notification-vehicle-name">
                {{ event.metadata?.['vehicleName'] }}
            </h2>
            <ce-parameter-list size="small">
                <ce-parameter-item [orientation]="'horizontal'">
                    <span ceParameterLabel>Event occurred</span>
                    <span ceParameterItemValue class="u-text-align-right">{{ event.formattedTimestamp }}</span>
                </ce-parameter-item>
                @if (notification().eventType === 'cannot-reach-target-charge' || notification().eventType === 'scheduled-session-ended-unexpectedly') {
                    <ce-parameter-item [orientation]="'horizontal'">
                        <span ceParameterLabel>Target SoC</span>
                        <span ceParameterItemValue class="u-text-align-right">{{ event.metadata?.['targetSoC'] }} %</span>
                    </ce-parameter-item>
                    @if (notification().eventType === 'cannot-reach-target-charge') {
                        <ce-parameter-item [orientation]="'horizontal'">
                            <span ceParameterLabel>Forecasted end SoC</span>
                            <span ceParameterItemValue class="u-text-align-right">{{ event.metadata?.['expectedSoC'] | truncateSoC }} %</span>
                        </ce-parameter-item>
                    }
                    @if (notification().eventType === 'scheduled-session-ended-unexpectedly') {
                        <ce-parameter-item [orientation]="'horizontal'">
                            <span ceParameterLabel>Actual end SoC</span>
                            <span ceParameterItemValue class="u-text-align-right">{{ event.metadata?.['latestSoC'] | truncateSoC }} %</span>
                        </ce-parameter-item>
                    }
                }
                <ce-parameter-item [orientation]="'horizontal'">
                    <span ceParameterLabel>Event resolved</span>
                    @if (notification().eventType === 'scheduled-session-ended-unexpectedly') {
                        <span ceParameterItemValue class="u-text-placeholder u-text-align-right">Not applicable</span>
                    } @else if (event.resolvedTimestamp) {
                        <span ceParameterItemValue class="u-text-align-right">{{ event.formattedResolvedTimestamp }}</span>
                    } @else {
                        <span ceParameterItemValue class="u-text-placeholder u-text-align-right">Not yet resolved</span>
                    }
                </ce-parameter-item>
                @if (event.resolvedTimestamp && event.resolutionReason) {
                    <ce-parameter-item [orientation]="'horizontal'">
                        <span ceParameterLabel>Resolution reason</span>
                        <span ceParameterItemValue class="u-text-align-right">{{ event.resolutionReason }}</span>
                    </ce-parameter-item>
                }
            </ce-parameter-list>
        </div>
    }
}
